import React, { useState, useCallback, useEffect } from 'react';
import VoiceRecorder from './VoiceRecorder';
import { submitFeedback } from '../../services/feedback.service';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'sonner';

interface FeedbackSheetProps {
  isOpen: boolean;
  onClose: () => void;
  screenName?: string;
  hasFormData?: boolean;
}

export default function FeedbackSheet({ isOpen, onClose, screenName, hasFormData }: FeedbackSheetProps) {
  const [text, setText] = useState('');
  const [isVoice, setIsVoice] = useState(false);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();

  // Reset state when sheet closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setText('');
        setIsVoice(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleTranscript = useCallback((transcript: string) => {
    setText(transcript);
    setIsVoice(true);
  }, []);

  const handleSend = async () => {
    if (!text.trim() || !user?.id) return;

    setSending(true);
    try {
      await submitFeedback({
        text: text.trim(),
        is_voice: isVoice,
        user_id: user.id,
        user_name: user.name || 'Anonymous',
        screen_name: screenName,
        has_form_data: hasFormData,
      });
      toast.success('Thanks for your feedback!');
      onClose();
    } catch (err) {
      toast.error('Failed to send feedback. Try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[2rem] shadow-botanical-lg animate-in slide-in-from-bottom-4 duration-300 p-5 pb-8">
        <div className="w-9 h-1 bg-aura-text/20 rounded-full mx-auto mb-4" />

        <h3 className="text-lg font-extrabold text-aura-text mb-1">Send Feedback</h3>
        {screenName && (
          <div className="flex items-center gap-1.5 mb-4">
            <span className="text-[10px] font-bold text-aura-text/40 bg-aura-background px-2 py-0.5 rounded-full">
              {screenName}
            </span>
            {hasFormData && (
              <span className="text-[10px] font-medium text-aura-primary/50 bg-aura-primary/5 px-2 py-0.5 rounded-full">
                data input included
              </span>
            )}
          </div>
        )}
        <p className="text-xs text-aura-text/40 mb-4 font-medium -mt-1">
          Suggestions, bugs, or anything on your mind
        </p>

        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (isVoice) setIsVoice(false);
          }}
          placeholder="Type your feedback here..."
          rows={4}
          className="w-full resize-none bg-aura-background rounded-xl p-3.5 text-sm text-aura-text placeholder:text-aura-text/30 border-none outline-none ring-1 ring-transparent focus:ring-aura-primary/30 transition-all"
          disabled={sending}
        />

        <div className="flex items-center justify-between mt-3">
          <VoiceRecorder
            onTranscript={handleTranscript}
            disabled={sending}
          />

          {isVoice && text && (
            <span className="text-[10px] font-medium text-aura-primary/60 mr-auto ml-2">
              Transcribed from voice
            </span>
          )}

          <button
            onClick={handleSend}
            disabled={!text.trim() || sending || !user?.id}
            className="px-5 py-2 rounded-full bg-gradient-to-r from-aura-primary to-aura-secondary text-white text-sm font-extrabold shadow-md shadow-aura-primary/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 hover:shadow-lg"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
